import {Link} from "react-router-dom";
import getTripTitle from "@/utils/getTripTitle.js";
import AddToFavorites from "@/components/hero/hero-1/AddToFavorites.jsx";
import reportEvent from "@/utils/reportEvent.js";

const TourProperties = ({toursData, suppressNoHotelBanner = false, searchQuery}) => {

  function renderTags(item) {
    if(!item?.tags) return;
    let result = [];
    for (let i = 0; i < item?.tags.length; i++) {
      let tag = item?.tags[i];
      result.push(
          <>
            <div className="col-auto">
              <p className="text-14 lh-14 mb-5">
                {tag}
              </p>
            </div>
            {/* End col-auto */}
            {i !== item?.tags.length - 1 && (
                <div className="col-auto">
                  <div className="size-3 rounded-full bg-light-1 mb-5"></div>
                </div>
            )}
          </>
      );
    }
    return result;
  }

  function getFlightTime(outboundFlight) {
    let date = new Date(...outboundFlight.departureDate).toLocaleTimeString();
    let splitted = date.split(':');
    if(splitted[0] === '00') return '';
    return ' ' + splitted[0] + ':' + splitted[1];
  }

  function getShortDayName(date) {
    return date.toLocaleDateString('pl-PL', {weekday: 'short'});
  }

  function getHotelIfPresent(startDateDayOfWeek, startDate, endDateDayOfWeek, endDate, hotel) {
    if (!hotel) return '';
    return <>
      📅 Data pobytu: {startDateDayOfWeek} {startDate} - {endDateDayOfWeek} {endDate} <br></br><br></br>
      🏨 Hotel: {hotel.name}, {hotel?.rating}/10, {hotel?.numberOfOpinions} opinii,
      {hotel?.price?.amount} zł<br></br><br></br>
    </>;
  }

  function getDate(item) {
    let result = new Date(...item.dateRange.start);
    result.setMonth(result.getMonth()-1)
    return result;
  }

  function getEndDate(item) {
    let result = new Date(...item.dateRange.end);
    result.setMonth(result.getMonth()-1)
    return result;
  }

  function getItenerarySummary(item) {
    const hotel = item.hotel;
    const startDate = getDate(item).toLocaleDateString();
    const startDateDayOfWeek = getDate(item).toLocaleDateString('pl-PL', {weekday: 'long'});
    const endDate = getEndDate(item).toLocaleDateString();
    const endDateDayOfWeek = getEndDate(item).toLocaleDateString('pl-PL', {weekday: 'long'});
    const outboundFlight = item.flight.outboundFlight;
    const returnFlight = item.flight.returnFlight;
    const pricePerPerson = Math.round(item.pricePerPerson);
    const pricePerDay = Math.round(item.priceWithCurrencyPerDay.amount);


    let departureDate = new Date(...outboundFlight.departureDate);
    departureDate.setMonth(departureDate.getMonth()-1)
    let returnDate = new Date(...returnFlight.departureDate);
    returnDate.setMonth(returnDate.getMonth()-1)
    return <p>
      {getHotelIfPresent(startDateDayOfWeek, startDate, endDateDayOfWeek, endDate, hotel)}

      ✈️ Lot
      do: {outboundFlight.departureAirportCity} ({outboundFlight.departureAirport}) - {item.destination.name} ({outboundFlight.arrivalAirport}), {getShortDayName(departureDate)} {departureDate.toLocaleDateString()}{getFlightTime(outboundFlight)}, {outboundFlight.company}, {outboundFlight.price.amount} zł<br></br>
      ✈️ Lot
      powrotny: {item.destination.name} ({returnFlight.departureAirport}) - {returnFlight.arrivalAirportCity} ({returnFlight.arrivalAirport}), {getShortDayName(returnDate)} {returnDate.toLocaleDateString()}{getFlightTime(returnFlight)}, {returnFlight.company}, {returnFlight.price.amount} zł<br></br>

      <br></br>
      💰 Cena za całość: {Math.round(item.price)} zł<br></br>
      💰 Cena za osobę: {pricePerPerson} zł<br></br>
      💰 Cena za dzień: {pricePerDay} zł<br></br>
    </p>;
  }

  // "Broaden the search" link for the no-hotel-results banner below: same query the page is
  // already running, just with the duration/weekend/uniqueness constraints that most commonly
  // rule out hotel matches relaxed, and the date window pushed out further. Kept deliberately
  // simple (a couple of param tweaks, not a real alternative-search algorithm) - a fragile
  // "smart" broadening isn't worth it for a single fallback link.
  function getBroaderSearchUrl() {
    if (!searchQuery) return null;
    const params = new URLSearchParams(searchQuery);
    params.delete("durationMin");
    params.delete("durationMax");
    params.delete("onlyWeekends");
    params.delete("uniqueDestinations");
    const currentDateTo = params.get("dateTo");
    if (currentDateTo) {
      const extended = new Date(currentDateTo);
      if (!isNaN(extended.getTime())) {
        extended.setMonth(extended.getMonth() + 2);
        params.set("dateTo", extended.toISOString().slice(0, 10));
      }
    }
    return `/tour-list-v1?${params.toString()}`;
  }

  function getFlightsOnlyUrl() {
    if (!searchQuery) return null;
    return `/flights?${new URLSearchParams(searchQuery).toString()}`;
  }

  function getHeadingIfHotelEmpty() {
    if (toursData.length === 0) {
      return <h3 className="text-18 lh-16 fw-500">Brak wyników. Spróbuj rozszerzyć kryteria wyszukiwania i spróbuj ponownie</h3>;
    }
    if(suppressNoHotelBanner) return;
    if(toursData[0].hotel) return;
    const broaderSearchUrl = getBroaderSearchUrl();
    const flightsOnlyUrl = getFlightsOnlyUrl();
    return   <div style={{
      backgroundColor: 'rgba(231,152,27,0.54)', // This is for a warning orange color
      color: 'black', // This is for the text color
      padding: '10px', // This is for padding around the text
      borderRadius: '5px', // This is to make the corners rounded
      margin: '10px 0', // This is to add some space above and below the div
    }}>
      <h3 className="text-18 lh-16 fw-500 ">Dla podanych parametrów nie znaleźliśmy okazyjnych wycieczek z hotelami, co
        oznacza że ceny w podanym okresie są wysokie, lub parametry wyszukiwania nie pozwalają stworzyć optymalnej kombinacji.
        <br></br>Udało nam się jednak znaleźć loty spełniające zadane parametry</h3>
      {(broaderSearchUrl || flightsOnlyUrl) && (
        <div className="d-flex x-gap-15 y-gap-10 flex-wrap mt-15">
          {broaderSearchUrl && (
            <Link to={broaderSearchUrl} className="button -sm -dark-1 bg-blue-1 text-white">
              Rozszerz wyszukiwanie <div className="icon-arrow-top-right ml-10"></div>
            </Link>
          )}
          {flightsOnlyUrl && (
            <Link to={flightsOnlyUrl} className="button -sm -blue-1 border-blue-1 text-blue-1">
              Szukaj samych lotów <div className="icon-arrow-top-right ml-10"></div>
            </Link>
          )}
        </div>
      )}
    </div> ;
  }

  function getActionButtons(item) {
    if(item?.hotel) {
      return <Link
          to={`/tour-single/${item.id}`}
          className="button -md -dark-1 bg-blue-1 text-white mt-24"
          onClick={ () => {
            reportEvent({
              category: "button-click",
              action: "view-offer-button-clik"
            })
          }
          }
      >
        Zobacz ofertę 👀<div className="icon-arrow-top-right ml-15"></div>
      </Link>;
    } else {
      // Flight-only: two separate reservation links (outbound/return legs go to different
      // airline booking pages), stacked with a real gap instead of two mt-24 buttons butted
      // against each other.
      return <div className="d-flex flex-column y-gap-10 mt-15">
        <Link
            to={`${item?.flight?.outboundFlight?.reservationUrl}`}
            className="button -md -dark-1 bg-blue-1 text-white"
        >
          Rezerwuj lot do {item?.destination?.toName || item?.destination?.name} <div className="icon-arrow-top-right ml-15"></div>
        </Link>
        <Link
            to={`${item?.flight?.returnFlight?.reservationUrl}`}
            className="button -md -dark-1 border-blue-1 text-blue-1 bg-blue-2"
        >
          Rezerwuj lot powrotny <div className="icon-arrow-top-right ml-15"></div>
        </Link>
      </div>;
    }
  }

  function getHotelReview(item) {
    if(!item?.hotel) return;
    return <>
      <div className="d-flex x-gap-5 items-center justify-end md:justify-start">
        ⭐ Ocena hotelu: {item?.hotel?.rating}/10
      </div>

      <div className="text-14 lh-14 text-light-1 mt-10">
        {item?.hotel?.numberOfOpinions} opinii
      </div>
    </>;
  }

  function getTripTitleIfHotelPresent(item) {
    if(item?.hotel) return <a href={'/tour-single/' + item?.id}>{getTripTitle(item)}</a>;
    return getTripTitle(item);
  }

  function getDealLabel(item) {
    const average = Number(item?.averagePricePerDay);
    const pricePerDay = Number(item?.pricePerDay ?? item?.priceWithCurrencyPerDay?.amount);
    const pricePerPerson = Math.round(Number(item?.pricePerPerson));
    const rating = Number(item?.hotel?.rating);
    const opinions = Number(item?.hotel?.numberOfOpinions);
    const outboundPrice = Number(item?.flight?.outboundFlight?.price?.amount);
    const returnPrice = Number(item?.flight?.returnFlight?.price?.amount);
    const flightTotal = outboundPrice + returnPrice;
    const duration = item?.dateRange ? Math.round((getEndDate(item) - getDate(item)) / 86400000) + 1 : 0;

    if (item?.isGreatOffer && average > pricePerDay) {
      const saving = Math.round(((average - pricePerDay) / average) * 100);
      return <>🔥 Cenowy strzał · <b>{saving}% poniżej zwykłej ceny</b></>;
    }
    if (flightTotal > 0 && flightTotal <= 120) {
      return <>🛫 Lotniczy jackpot · <b>loty w obie strony za {Math.round(flightTotal)} zł/os.</b></>;
    }
    if (rating >= 9 && opinions >= 200 && pricePerDay <= 220) {
      return <>⭐ Hotelowy pewniak · <b>{rating}/10 z {opinions} opinii</b></>;
    }
    if (duration > 0 && duration <= 4 && pricePerPerson <= 400) {
      return <>⚡ City break w punkt · <b>{pricePerPerson} zł/os.</b></>;
    }
    if (pricePerDay > 0 && pricePerDay <= 100) {
      return <>💎 Rzadko spotykana cena · <b>{Math.round(pricePerDay)} zł za dzień</b></>;
    }
    return null;
  }

  function getImageWrapper(item) {
    // destination.photoUrl comes from the curated destinations CMS list and is "" (not
    // undefined/null - so a plain `||` on the whole item.destination works, but the field
    // itself needs an explicit falsy check) for destinations that aren't in that list yet.
    // That's common for flight-only results, since those can point at any airport Ryanair
    // flies to, not just our curated hotel destinations - so render a themed placeholder
    // (airplane glyph on a brand gradient) instead of a broken/blank <img> with an empty src.
    const photoUrl = item?.destination?.photoUrl || item?.destination?.wordpressPhoto;
    const fallback = "/img/flights/flight-deal-fallback.webp";
    const image = (
      <div className="cardImage__content custom_inside-slider">
        <img
            className="rounded-4 js-lazy img-fluid w-100"
            src={photoUrl || fallback}
            alt={item?.destination?.name ?? "image"}
            loading="lazy"
            decoding="async"
            onError={event => {if (!event.currentTarget.src.endsWith(fallback)) event.currentTarget.src = fallback}}
        />
      </div>
    );
    // Flight-only results (no hotel) have no id and no /tour-single page to link to.
    if(!item?.hotel) return image;
    return (
      <a href={'/tour-single/' + item?.id} onClick={
        () => {
            reportEvent({
                category: "button-click",
                action: "tour-image-click"
            })
            }
      }>
        {image}
      </a>
    );
  }

  return (
    <>
      {getHeadingIfHotelEmpty()}
      {toursData.map((item, i) => (
        <div
            className="col-12"
            key={item?.id ?? `flight-${i}`}
            data-aos="fade"
            data-aos-delay={item?.delayAnimation}
        >
          <div className="border-top-light pt-30">
            <div className="row x-gap-20 y-gap-20">
              <div className="col-md-auto">
                <div className="cardImage ratio ratio-1:1 w-300 md:w-1/1 rounded-4">
                  {getImageWrapper(item)}
                  {/* End .cardImage__content */}

                  <AddToFavorites id={item?.id}/>
                  {/* End .cardImage__wishlist */}
                </div>
                {/* End cartImage */}
              </div>
              {/* End .col-auto */}

              <div className="col-md">
                <div className="row x-gap-10 items-center">
                  {renderTags(item)}
                  {/* End col-auto */}

                  <div className="col-auto">
                    <p className="text-14 lh-14 mb-5">{item?.tourType}</p>
                  </div>
                  {/* End col-auto */}
                </div>
                {/* End .row */}
                <h3 className="text-18 lh-16 fw-500">
                  {getTripTitleIfHotelPresent(item)}
                </h3>
                <div>
                  {getDealLabel(item) && (
                      <div className="text-14 lh-14 text-green-2 fw-500 mt-5 p-2 bg-light-2 rounded-2">
                        {getDealLabel(item)}
                      </div>
                  )}
                </div>
                {/*<p className="text-14 lh-14 mt-5">{item?.location}</p>*/}
                <div className="text-14 lh-15 fw-500 mt-20">
                  {getItenerarySummary(item)}
                </div>
                <div className="text-14 text-green-2 fw-500 lh-15 mt-5">
                  {/*Free cancellation*/}
                </div>
              </div>
              {/* End col-md */}

              <div className="col-md-auto text-right md:text-left">
                {getHotelReview(item)}

                {/* mt-50 pushes the price below the hotel rating block above it - only needed
                    when that block is actually rendered (item?.hotel present). */}
                <div className={`text-14 text-light-1 ${item?.hotel ? "mt-50 md:mt-20" : "mt-0"}`}>Cena wycieczki</div>
                <div className="text-22 lh-12 fw-600 mt-5">
                  {Math.round(item?.pricePerPerson)} zł
                </div>
                <div className="text-14 text-light-1 mt-5">za osobę</div>
                {/* Same "is this price real" trust line as the trip-single page's price block
                    (SidebarRight.jsx) - kept to one short line here so it doesn't crowd the card. */}
                <div className="text-11 text-light-1 mt-5">Cena końcowa, bez ukrytych opłat</div>

                {getActionButtons(item)}
              </div>
              {/* End col-md */}
            </div>
            {/* End .row */}
          </div>
          {/* End border-top */}
        </div>
      ))}
    </>
  );
};

export default TourProperties;
